import Button from "components/atoms/buttons/Button";
import moment from "moment";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { isMediaOfTypeAudioOrVideo, isMediaOfTypeDocument, isMediaOfTypeImage } from "utils/helpers";
import { useLoaderContext } from "components/context/LoaderContext";
import { getAirDropEndRoute } from "apis/magnets";
import { toast } from "react-toastify";
import { post } from "utils/request";
import ButtonWithIcon from "components/atoms/buttons/ButtonWithIcon";
import { RefreshIcon } from "components/atoms/Icon";
import { clsx } from "clsx";
import { getEndClaimCodeEndPoint } from "apis/claimCode";
import ShareRequestModal from "components/organisms/ShareRequest/ShareRequestModal";
import { MementoModalUI } from "pages/AirdropMemento/PreviewMementoModal";
import { getEndMerchDropEndpoint } from "../../../../apis/merchdrop";
import PollResultsModalContainer from "../../../../components/organisms/poll/PollResultsModalContainer";
import NewActivationModal from "./Components/NewActivationModal";
import ActivationTable from "./Component/ActivationTable";
import { TIME_FORMAT, DATE_FORMAT, TOAST_CONFIG } from "../../../../constants";

function MementoPreviewer({
  memento,
  onClose,
  handleEndAirDrop,
}: {
  memento: Memento;
  onClose: any;
  handleEndAirDrop: (mementoId: string, magnetId: string, mementoKey: string) => void;
}) {
  // @ts-ignore
  const isFileVideo = isMediaOfTypeAudioOrVideo(memento?.media_url);
  const isFileImage = isMediaOfTypeImage(memento?.media_url as string);
  const isFileDocument = isMediaOfTypeDocument(memento?.media_url as string);

  const momentDate = moment(memento.start_time);
  const airdropStartTime = momentDate.format(TIME_FORMAT);
  const airdropStartDate = momentDate.format(DATE_FORMAT);

  const getMementoModalActionButtons = useMemo(() => {
    switch (memento.status) {
      case 1:
      case 3:
        return (
          <div className="flex h-12 space-x-4">
            <Button text="CANCEL" additionalClasses="sm:px-14" variant="whiteOutline" onClick={onClose} />
            <Button
              text="END DROP"
              additionalClasses="sm:px-14"
              variant="magnetiqSolid"
              onClick={() => handleEndAirDrop(memento.id, memento.magnet_id, memento.activation_key)}
            />
          </div>
        );
      default:
        return null;
    }
  }, [memento.status]);

  return (
    <MementoModalUI
      open
      isActivationTablePreview
      isFileImage={isFileImage}
      isFileDocument={isFileDocument}
      isFileAudioOrVideo={isFileVideo}
      onClose={onClose}
      actionButtons={getMementoModalActionButtons}
      airdropStartTime={airdropStartTime}
      airdropStartDate={airdropStartDate}
      magnetImage={memento.magnet_image_url}
      magnetName={memento.magnet_name}
      brandName={memento.brand_name}
      mediaUrl={memento.media_url as string}
      mementoName={memento.name}
      mementoDesc={memento.description}
      thumbnailUrl={memento.thumbnail_url}
    />
  );
}

type Props = {
  mementosForMagnet: Memento[];
  availableActivations: Activation[];
  isCreateActivationModalVisible: boolean;
  setIsCreateActivationModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleOnClickActivateActivation(activation?: string): void;
  handleOnClickManage(momemntoId: string, mementoActivationKey: string): void;
  handlePurchaseActivation: (activationId: string) => void;
  handleRefetchActivations: () => void;
  triggerRefetchMagnetMementos: () => void;
  isRefreshingMementoForMagnet: boolean;
  isMagnetPublished: boolean;
};

export default function ActivationComponent({
  isCreateActivationModalVisible,
  setIsCreateActivationModalVisible,
  handleOnClickActivateActivation,
  availableActivations,
  mementosForMagnet,
  handleOnClickManage,
  handlePurchaseActivation,
  handleRefetchActivations,
  triggerRefetchMagnetMementos,
  isRefreshingMementoForMagnet,
  isMagnetPublished,
}: Props) {
  const { setValue, reset, watch, getValues } = useForm<{
    activation: undefined | string;
  }>({
    defaultValues: { activation: "mem_drop" },
  });

  const { setLoading } = useLoaderContext();

  const [currentlyViewingMementoId, setCurrentlyViewingMementoId] = useState<string | null>(null);
  const [pollId, setPollId] = useState<string | null>(null);
  const [shareRequestId, setShareRequestId] = useState<string | null>(null);

  const onCloseCreateActivationModal = () => {
    setIsCreateActivationModalVisible(false);
    reset();
  };

  const handleViewPollResults = (pollID: string) => setPollId(pollID);

  const handleShareRequestPreview = (id: string) => setShareRequestId(id);

  const handleOnClickActivate = () => {
    const selectedActivation = getValues("activation");
    handleOnClickActivateActivation(selectedActivation);
  };

  const mementoToRenderInPreviewModal = useMemo(
    () => mementosForMagnet.find((mem) => mem.id === currentlyViewingMementoId),
    [currentlyViewingMementoId, mementosForMagnet],
  );

  const shareRequestToRenderInPreviewModal = useMemo(
    () => mementosForMagnet.find((mem) => mem.id === shareRequestId),
    [shareRequestId, mementosForMagnet],
  );

  const handleEndAirDrop = async (mementoId: string, magnetId: string, mementoKey: string) => {
    try {
      // TODO: Pass a prop for data
      if (!mementoId || !magnetId) {
        throw new Error("Invalid data found...");
      }

      setLoading(true);

      const handleEndDropError = (errorMessage: string) => {
        setLoading(false);
        toast.error(errorMessage, TOAST_CONFIG);
      };

      const handleRefetchDataAndCloseModal = () => {
        setLoading(false);
        triggerRefetchMagnetMementos();
        setCurrentlyViewingMementoId(null);
      };

      switch (mementoKey) {
        case "mem_drop":
          post(
            getAirDropEndRoute(magnetId, mementoId),
            {},
            {
              processData: () => {
                toast.success("Ended Airdrop", TOAST_CONFIG);
                handleRefetchDataAndCloseModal();
              },
              processError: handleEndDropError,
            },
          );
          break;
        case "merch_drop":
          post(
            getEndMerchDropEndpoint(magnetId, mementoId),
            {},
            {
              processData: () => {
                toast.success("Ended Merchdrop", TOAST_CONFIG);
                handleRefetchDataAndCloseModal();
              },
              processError: handleEndDropError,
            },
          );
          break;
        case "claim_code":
          post(
            getEndClaimCodeEndPoint(magnetId, mementoId),
            {},
            {
              processData: () => {
                toast.success("Ended Claimdrop", TOAST_CONFIG);
                handleRefetchDataAndCloseModal();
              },
              processError: handleEndDropError,
            },
          );
          break;
        default:
          toast.error("Invalid memento key", TOAST_CONFIG);
      }
    } catch (err) {
      toast.error(err?.toString(), TOAST_CONFIG);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex w-auto">
        <div className="my-4 flex w-full justify-between">
          <ButtonWithIcon
            icon={<RefreshIcon className={clsx("h-6 w-5", isRefreshingMementoForMagnet && "animate-spin")} />}
            label="Refresh"
            labelClasses="!font-semibold text-xs 2xl:text-sm"
            onClick={triggerRefetchMagnetMementos}
          />
          <div className="flex gap-2">
            <Button
              disabled={!isMagnetPublished}
              additionalClasses="text-base md:text-xs xl:text-xs 2xl:text-sm px-3 py-1 xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
              variant="magnetiqSolid"
              text="CREATE ACTIVATION"
              onClick={() => setIsCreateActivationModalVisible(true)}
            />
          </div>
          <NewActivationModal
            setValue={setValue}
            open={isCreateActivationModalVisible}
            onClose={onCloseCreateActivationModal}
            availableActivations={availableActivations}
            selectedActivation={watch("activation")}
            onClickActivate={handleOnClickActivate}
            handlePurchaseActivation={handlePurchaseActivation}
          />
          {mementoToRenderInPreviewModal && (
            <MementoPreviewer
              memento={mementoToRenderInPreviewModal}
              onClose={() => setCurrentlyViewingMementoId(null)}
              handleEndAirDrop={handleEndAirDrop}
            />
          )}
          {pollId && (
            <PollResultsModalContainer
              pollId={pollId}
              onModalClose={() => setPollId(null)}
              handleRefetchActivations={handleRefetchActivations}
            />
          )}
          {shareRequestId && (
            <ShareRequestModal
              isBrandPreview
              id={shareRequestToRenderInPreviewModal?.id as string}
              brandId={shareRequestToRenderInPreviewModal?.brand_id as string}
              name={shareRequestToRenderInPreviewModal?.name as string}
              description={shareRequestToRenderInPreviewModal?.description as string}
              thumbnailUrl={shareRequestToRenderInPreviewModal?.thumbnail_url as string}
              mediaUrl={shareRequestToRenderInPreviewModal?.media_url}
              mediaType={shareRequestToRenderInPreviewModal?.media_type}
              onClose={() => setShareRequestId(null)}
              postData={shareRequestToRenderInPreviewModal?.metadata}
              magnetId={shareRequestToRenderInPreviewModal?.magnet_id as string}
              magnetImageUrl={shareRequestToRenderInPreviewModal?.magnet_image_url as string}
              magnetName={shareRequestToRenderInPreviewModal?.magnet_name as string}
              brandName={shareRequestToRenderInPreviewModal?.brand_name as string}
              startTime={shareRequestToRenderInPreviewModal?.start_time as number}
              endTime={shareRequestToRenderInPreviewModal?.end_time as number}
              autoAirdropOnPurchase={shareRequestToRenderInPreviewModal?.auto_drop_on_purchase as boolean}
              publishHandler={() => {}}
              dropStatus={shareRequestToRenderInPreviewModal?.status as MementoStatus}
            />
          )}
        </div>
      </div>
      <ActivationTable
        isMagnetPublished={isMagnetPublished}
        handleCreateActivation={() => setIsCreateActivationModalVisible(true)}
        mementosForMagnet={mementosForMagnet}
        onClickManage={handleOnClickManage}
        setMementoForViewingMemento={setCurrentlyViewingMementoId}
        handleViewPollResults={handleViewPollResults}
        handleShareRequestPreview={handleShareRequestPreview}
      />
    </>
  );
}
